import React, { useEffect, useState } from "react";
import { AdminEvent, EventSetting } from "./types";
import "./AdminEvent.css";

type SettingType = "bit" | "int" | "string" | "datetime";

const DEFAULT_EVENT_SETTINGS: EventSetting[] = [
    { settingKey: "PersonBillede", settingValue: "0", settingType: "bit" },
    { settingKey: "ErDerZoner", settingValue: "0", settingType: "bit" },
    { settingKey: "Sprog", settingValue: "en", settingType: "string" },
    { settingKey: "LokalMailPerson", settingValue: "defaultmail@badminton.dk", settingType: "string" },
    { settingKey: "AntalVagterPrDag", settingValue: "3", settingType: "int" },
    { settingKey: "Form_Accreditations", settingValue: "0", settingType: "bit" },
    { settingKey: "Form_Practice", settingValue: "0", settingType: "bit" },
    { settingKey: "Form_Visa", settingValue: "0", settingType: "bit" },
    { settingKey: "Form_Transport", settingValue: "0", settingType: "bit" },
    { settingKey: "Form_Hotel", settingValue: "0", settingType: "bit" },
    { settingKey: "Form_Accreditations_Deadline", settingValue: "", settingType: "datetime" },
    { settingKey: "Form_Practice_Deadline", settingValue: "", settingType: "datetime" },
    { settingKey: "Form_Visa_Deadline", settingValue: "", settingType: "datetime" },
    { settingKey: "Form_Transport_Deadline", settingValue: "", settingType: "datetime" },
    { settingKey: "Form_Hotel_Deadline", settingValue: "", settingType: "datetime" },
];

const mergeEventSettings = (existing?: EventSetting[]): EventSetting[] => {
    if (!existing || existing.length === 0) return [...DEFAULT_EVENT_SETTINGS];
    const map = new Map<string, EventSetting>();
    existing.forEach(s => map.set(s.settingKey, s));
    DEFAULT_EVENT_SETTINGS.forEach(d => { if (!map.has(d.settingKey)) map.set(d.settingKey, d); });
    return Array.from(map.values());
};

interface EventFormProps {
    event: AdminEvent;
    onSave: (event: AdminEvent) => void;
    onCancel: () => void;
}

const EventForm: React.FC<EventFormProps> = ({ event, onSave, onCancel }) => {
    const [formData, setFormData] = useState<AdminEvent>({
        ...event,
        startTime: event.startTime ?? "",
        endTime: event.endTime ?? "",
        eventSettings: mergeEventSettings(event.eventSettings),
    });

    const [showSettings, setShowSettings] = useState(false);

    useEffect(() => {
        setFormData({
            ...event,
            startTime: event.startTime ?? "",
            endTime: event.endTime ?? "",
            eventSettings: mergeEventSettings(event.eventSettings),
        });
    }, [event]);

    const updateSettingValue = (index: number, value: string) => {
        setFormData(prev => ({
            ...prev,
            eventSettings: (prev.eventSettings ?? []).map((s, i) => i === index ? { ...s, settingValue: value } : s)
        }));
    };

    const renderSettingInput = (setting: EventSetting, index: number) => {
        switch (setting.settingType.toLowerCase()) {
            case "bit":
                return (
                    <label className="toggle">
                        <input
                            type="checkbox"
                            checked={setting.settingValue === "1"}
                            onChange={(e) => updateSettingValue(index, e.target.checked ? "1" : "0")}
                        />
                        <span className="toggle-track" aria-hidden="true" />
                    </label>
                );
            case "int":
                return <input type="number" value={setting.settingValue} onChange={e => updateSettingValue(index, e.target.value.replace(/\D/g, ""))} />;
            case "datetime":
                return (
                    <input
                        type="datetime-local"
                        value={
                            setting.settingValue
                                ? toDateTimeLocalValue(setting.settingValue)
                                : ""
                        }
                        onChange={(e) =>
                            updateSettingValue(
                                index,
                                fromDateTimeLocalValue(e.target.value)
                            )
                        }
                    />
                );
            default:
                return <input type="text" value={setting.settingValue} onChange={e => updateSettingValue(index, e.target.value)} />;
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (formData.startTime && formData.endTime) {
            const s = new Date(formData.startTime).getTime();
            const en = new Date(formData.endTime).getTime();
            if (en <= s) {
                alert("Slut tidspunkt skal være efter start tidspunkt.");
                return;
            }
        }

        onSave(formData);
    };

    const pad2 = (n: number) => String(n).padStart(2, "0");

    const toDateTimeLocalValue = (input: string) => {
        if (!input) return "";
        const d = new Date(input);
        if (isNaN(d.getTime())) return "";

        const yyyy = d.getFullYear();
        const mm = pad2(d.getMonth() + 1);
        const dd = pad2(d.getDate());
        const hh = pad2(d.getHours());
        const mi = pad2(d.getMinutes());

        return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
    };

    const fromDateTimeLocalValue = (value: string) => {
        if (!value) return "";
        return new Date(value).toISOString();
    };

    const compressImage = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => {
                const img = new Image();

                img.onload = () => {
                    const maxWidth = 600;
                    const maxHeight = 278;
                    const quality = 1;

                    let { width, height } = img;

                    if (width > maxWidth || height > maxHeight) {
                        const ratio = Math.min(maxWidth / width, maxHeight / height);
                        width = Math.round(width * ratio);
                        height = Math.round(height * ratio);
                    }

                    const canvas = document.createElement("canvas");
                    canvas.width = width;
                    canvas.height = height;

                    const ctx = canvas.getContext("2d");
                    if (!ctx) {
                        reject(new Error("Kunne ikke oprette canvas context"));
                        return;
                    }

                    ctx.drawImage(img, 0, 0, width, height);

                    resolve(canvas.toDataURL("image/jpeg", quality));
                };

                img.onerror = () => reject(new Error("Kunne ikke indlæse billede"));
                img.src = reader.result as string;
            };

            reader.onerror = () => reject(new Error("Kunne ikke læse fil"));
            reader.readAsDataURL(file);
        });
    };


    return (
        <div className="eventform-page">
            <form className="eventform-card" onSubmit={handleSubmit}>
                <div className="eventform-header">
                    <h2 className="eventform-title">
                        {formData.eventID ? "Opdater Event" : "Opret Event"}
                    </h2>
                    <p className="eventform-subtitle">Udfyld oplysninger og gem ændringer.</p>
                </div>

                <div className="eventform-grid">
                    <div className="field">
                        <label>Event Navn</label>
                        <input
                            type="text"
                            value={formData.eventName}
                            onChange={(e) => setFormData({ ...formData, eventName: e.target.value })}
                            required
                        />
                    </div>

                    <div className="field field-inline">
                        <div className="check">
                            <span>Aktiv</span>
                            <label className="toggle">
                                <input
                                    type="checkbox"
                                    checked={formData.active}
                                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                                />
                                <span className="toggle-track" aria-hidden="true" />
                            </label>
                        </div>
                        <div className="check">
                            <span>Intern</span>
                            <label className="toggle">
                                <input
                                    type="checkbox"
                                    checked={formData.isInternal}
                                    onChange={(e) => setFormData({ ...formData, isInternal: e.target.checked })}
                                />
                                <span className="toggle-track" aria-hidden="true" />
                            </label>
                        </div>
                    </div>

                    <div className="field">
                        <label>Start tidspunkt</label>
                        <input
                            type="datetime-local"
                            value={formData.startTime ? toDateTimeLocalValue(formData.startTime) : ""}
                            onChange={(e) => setFormData({ ...formData, startTime: fromDateTimeLocalValue(e.target.value) })}
                            required
                        />
                    </div>

                    <div className="field">
                        <label>Slut tidspunkt</label>
                        <input
                            type="datetime-local"
                            value={formData.endTime ? toDateTimeLocalValue(formData.endTime) : ""}
                            onChange={(e) => setFormData({ ...formData, endTime: fromDateTimeLocalValue(e.target.value) })}
                            required
                        />
                    </div>

                    <div className="field">
                        <label>Event Logo</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;

                                try {
                                    const compressed = await compressImage(file);
                                    setFormData(prev => ({
                                        ...prev,
                                        eventLogoPath: compressed
                                    }));
                                } catch (err) {
                                    alert("Kunne ikke behandle billedet.");
                                }
                            }}
                        />
                        {formData.eventLogoPath && (
                            <div className="preview">
                                <img src={formData.eventLogoPath} alt="Event Logo Preview" />
                            </div>
                        )}
                    </div>

                    <div className="field">
                        <label>Event ID Card</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    const reader = new FileReader();
                                    reader.onload = () => setFormData({ ...formData, eventIDCardPath: reader.result as string });
                                    reader.readAsDataURL(file);
                                }
                            }}
                        />
                        {formData.eventIDCardPath && (
                            <div className="preview">
                                <img src={formData.eventIDCardPath} alt="Event ID Card Preview" />
                            </div>
                        )}
                    </div>
                </div>

                <div className="eventform-divider" />

                <div className="eventform-actions">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowSettings((prev) => !prev)}
                    >
                        {showSettings ? "Skjul Event Settings" : "Vis Event Settings"}
                    </button>

                    <div className="actions-right">
                        <button className="btn btn-primary" type="submit">Gem</button>
                        <button className="btn btn-ghost" type="button" onClick={onCancel}>Annuller</button>
                    </div>
                </div>

                {showSettings && (formData.eventSettings ?? []).length > 0 && (
                    <div className="settings">
                        <h3>Event Settings</h3>
                        {(formData.eventSettings ?? []).map((s, i) => (
                            <div key={s.settingKey} className="settings-row">
                                <div className="settings-key">{s.settingKey}</div>
                                <div className="settings-input">{renderSettingInput(s, i)}</div>
                            </div>
                        ))}
                    </div>
                )}
            </form>
        </div>
    );
};

export default EventForm;